/**
 * AURA Event Bus
 * Pub/sub system for decoupled module communication
 */

import { AuraEventType } from './types';
import { Logger } from './logger';

type EventCallback = (data?: unknown) => void;

export class EventBus {
  private handlers: Map<string, EventCallback[]> = new Map();
  private logger = new Logger('EventBus');

  /**
   * Subscribe to a specific event type.
   * Returns an unsubscribe function to remove this handler.
   */
  on(type: AuraEventType | '*', callback: EventCallback): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, []);
    }
    this.handlers.get(type)!.push(callback);
    this.logger.debug(`Subscribed to ${type}`);

    return () => {
      const handlers = this.handlers.get(type);
      if (handlers) {
        const index = handlers.indexOf(callback);
        if (index !== -1) {
          handlers.splice(index, 1);
          this.logger.debug(`Unsubscribed from ${type}`);
        }
      }
    };
  }

  /**
   * Subscribe to an event type, but only trigger once
   */
  once(type: AuraEventType, callback: EventCallback): void {
    const unsubscribe = this.on(type, (data) => {
      unsubscribe();
      callback(data);
    });
  }

  /**
   * Remove all handlers for a specific event type
   */
  off(type: AuraEventType | '*'): void {
    this.handlers.delete(type);
    this.logger.debug(`Removed all handlers for ${type}`);
  }

  /**
   * Emit an event to all subscribers of the given type.
   * Data is passed directly to handlers.
   */
  emit(type: AuraEventType, data?: unknown): void {
    this.logger.debug(`Emitting event: ${type}`);

    const typeHandlers = this.handlers.get(type) ?? [];
    const wildcardHandlers = this.handlers.get('*') ?? [];
    const allHandlers = [...typeHandlers, ...wildcardHandlers];

    allHandlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        this.logger.error(`Error in event handler for ${type}`, error);
      }
    });
  }

  /**
   * Remove all subscriptions
   */
  clear(): void {
    this.handlers.clear();
    this.logger.debug('Cleared all subscriptions');
  }

  /**
   * Get total subscription count (for debugging)
   */
  getSubscriptionCount(): number {
    let count = 0;
    this.handlers.forEach((handlers) => (count += handlers.length));
    return count;
  }
}

// Singleton instance
export const eventBus = new EventBus();
